import type { RefObject } from "react";
import type { FernieFaceState } from "~/components/fernie/fernieMarkRules";

import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useLocation } from "react-router-dom";

import { getDockShellRoute } from "~/components/dock/dockShellRoutes";
import answerRating from "~/helpers/aiChat/answerRating";
import attachedContextLimit from "~/helpers/aiChat/attachedContextLimit";
import buildAssistantMessage from "~/helpers/aiChat/buildAssistantMessage";
import buildEntityContext from "~/helpers/aiChat/buildEntityContext";
import chatTurn from "~/helpers/aiChat/buildFilePrompt";
import chatHistory, { type HistoryRow } from "~/helpers/aiChat/chatHistory";
import chatStorage, { type Message, type MutationProposal, type SavedChat } from "~/helpers/aiChat/chatStorage";
import composeStreamError from "~/helpers/aiChat/composeStreamError";
import { type NavCandidate } from "~/helpers/aiChat/fernieActions";
import fernieActiveSession from "~/helpers/aiChat/fernieActiveSession";
import { type FernieRunOutcome } from "~/helpers/aiChat/fernieFaceState";
import { buildExecutionFeedback } from "~/helpers/aiChat/fernieFeedback";
import {
	declineResolution,
	type IntakeDraft,
	isSubmittedIntake,
	resolveIntakeSubmission
} from "~/helpers/aiChat/fernieIntake";
import migrationReviewRules from "~/helpers/aiChat/migrationReview";
import { isExternalPath } from "~/helpers/aiChat/navigationDestinations";
import parseFileToText from "~/helpers/aiChat/parseFileToText";
import resolveResumedChat from "~/helpers/aiChat/resumeChat";
import buildScreenContext from "~/helpers/aiChat/screenContext";
import setupFileRules from "~/helpers/aiChat/setupFiles";
import mergeStreamStep from "~/helpers/aiChat/streamSteps";
import toErrorMessage from "~/helpers/aiChat/toErrorMessage";
import buildWelcomeSuggestions, { resolveWelcomeCustomerName } from "~/helpers/aiChat/welcomeSuggestions";
import logger from "~/helpers/logger";
import unauthorizedRetryPolicy, { MUTATION_NOT_REPLAYED_MESSAGE } from "~/helpers/unauthorizedRetryPolicy";
import wsId from "~/helpers/wsId";
import { useAppDispatch, useAppSelector, useAppStore } from "~/hooks/reduxHooks";
import useCanOpenDestination from "~/hooks/useCanOpenDestination";
import useFernieFaceState from "~/hooks/useFernieFaceState";
import useFernieMigrationReview, { type UseFernieMigrationReviewReturn } from "~/hooks/useFernieMigrationReview";
import useFernieReportDraft, { type UseFernieReportDraftReturn } from "~/hooks/useFernieReportDraft";
import useFernieSetupFiles, { type UseFernieSetupFilesReturn } from "~/hooks/useFernieSetupFiles";
import {
	type AnswerRating,
	type AssistantTurn,
	type ChatModule,
	type ChatSessionSummary,
	type ChatTurnInput,
	cancelMutation,
	confirmMutation,
	deleteChatSession,
	fetchChatSessions,
	getChatSessionHistory,
	getLatestAnswer,
	openChatSession,
	type RatingDetails,
	rateChatMessage,
	type ScreenContext,
	StreamConnectionError,
	type StreamStepEvent,
	sendChatMessage,
	streamChatMessage
} from "~/services/aiOrchestrator";
import { apiEndpoints } from "~/services/endpoints/apiEndpoints";
import { enqueueActions, resetActionQueues } from "~/store/fernieActionSlice";
import { findWalkthroughsForRoute } from "~/walkthroughs/registry";
import { type WalkthroughDefinition } from "~/walkthroughs/types";

const { loadSaved, persistSaved, upsertSavedChat } = chatStorage;
const { buildHistoryRows, holdsEveryServerMessage, mapServerMessages, withServerAnswerFields } = chatHistory;
const { shouldAdoptOpenedSession, shouldOpenSession } = setupFileRules;
const { clearActiveSessionChatId, getActiveSessionChatId, setActiveSessionChatId } = fernieActiveSession;

export interface UseFernieChatOptions {
	focusOnOpen?: boolean;
	isActive: boolean;
}

export interface SendOptions {
	keepComposer?: boolean;
	opensOnboarding?: boolean;
}

export interface UseFernieChatReturn {
	messages: Message[];
	view: "chat" | "history";
	setView: React.Dispatch<React.SetStateAction<"chat" | "history">>;
	input: string;
	setInput: React.Dispatch<React.SetStateAction<string>>;
	loading: boolean;
	migrationReview: UseFernieMigrationReviewReturn;
	send: (text: string, options?: SendOptions) => Promise<void>;
	handleCancelStream: () => void;
	handleRegenerateReport: (filename: string) => void;
	pendingFile: { content: string; name: string; size: number } | null;
	setPendingFile: React.Dispatch<React.SetStateAction<{ content: string; name: string; size: number } | null>>;
	handleFile: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
	fileInputRef: RefObject<HTMLInputElement | null>;
	inputRef: RefObject<HTMLInputElement | null>;
	bottomRef: RefObject<HTMLDivElement | null>;
	conversationRef: RefObject<HTMLDivElement | null>;
	handleConversationScroll: () => void;
	setInputFocused: React.Dispatch<React.SetStateAction<boolean>>;
	setRunOutcome: React.Dispatch<React.SetStateAction<FernieRunOutcome | null>>;
	faceState: FernieFaceState;
	lastAssistantIndex: number;
	suggestions: string[];
	busyProposalId: string | null;
	handleConfirmProposal: (proposalId: string) => Promise<void>;
	handleCancelProposal: (proposalId: string) => Promise<void>;
	busyCaptureId: string | null;
	handleSubmitIntake: (captureId: string) => Promise<void>;
	handleCancelIntake: (captureId: string) => Promise<void>;
	handleChooseNav: (msgId: string, candidate: NavCandidate) => void;
	handleRateMessage: (message: Message, picked: AnswerRating, details?: RatingDetails) => Promise<boolean>;
	ratingPendingIds: string[];
	historyNotice: string | null;
	historyRows: HistoryRow[];
	chatId: string;
	hasHistory: boolean;
	historyLabel: string;
	handleNewChat: () => void;
	handleLoadChat: (row: HistoryRow) => Promise<void>;
	handleDeleteChat: (row: HistoryRow) => Promise<void>;
	confirmingDeleteId: string | null;
	setConfirmingDeleteId: React.Dispatch<React.SetStateAction<string | null>>;
	routeWalkthroughs: WalkthroughDefinition[];
	setupFiles: UseFernieSetupFilesReturn;
	sessionId: string | null;
	appendMessages: (messages: Message[]) => void;
	replaceAssistantMessage: (messageId: string, content: string) => void;
	reportDraft: UseFernieReportDraftReturn;
}

const useFernieChat = ({ focusOnOpen, isActive }: UseFernieChatOptions): UseFernieChatReturn => {
	const dispatch = useAppDispatch();
	const store = useAppStore();
	const { pathname } = useLocation();
	const canOpenDestination = useCanOpenDestination();
	const activeGridDataKey = useAppSelector((state) => state.fernieAction.activeGridDataKey);
	const activeGridSchema = useAppSelector((state) => state.fernieAction.activeGridSchema);
	const activeEntity = useAppSelector((state) => state.fernieAction.activeEntity);
	const executedActions = useAppSelector((state) => state.fernieAction.executedActions);
	const { i18n, t } = useTranslation("common");
	const { language } = i18n;
	const welcomeCustomerName = useAppSelector((state) => resolveWelcomeCustomerName(state[apiEndpoints.reducerPath]));

	const [saved, setSaved] = useState<SavedChat[]>(loadSaved);
	const [resumed] = useState(() => resolveResumedChat(saved, wsId(), getActiveSessionChatId()));
	const [view, setView] = useState<"chat" | "history">("chat");
	const [messages, setMessages] = useState<Message[]>(resumed.messages);
	const [ratingPendingIds, setRatingPendingIds] = useState<string[]>([]);
	const [input, setInput] = useState("");
	const [loading, setLoading] = useState(false);
	const [pendingFile, setPendingFile] = useState<{ content: string; name: string; size: number } | null>(null);
	const [chatId, setChatId] = useState(resumed.chatId);
	const [sessionId, setSessionId] = useState<string | null>(resumed.sessionId);
	const [busyProposalId, setBusyProposalId] = useState<string | null>(null);
	const [busyCaptureId, setBusyCaptureId] = useState<string | null>(null);
	const [runOutcome, setRunOutcome] = useState<FernieRunOutcome | null>(null);
	const [inputFocused, setInputFocused] = useState(false);
	const [serverSessions, setServerSessions] = useState<ChatSessionSummary[] | null>(null);
	const [hasSessionsLoadFailed, setHasSessionsLoadFailed] = useState(false);
	const [deleteFailureNotice, setDeleteFailureNotice] = useState<string | null>(null);
	const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
	const [opensOnboardingNext, dispatchOpensOnboarding] = useReducer(migrationReviewRules.nextOpensOnboarding, false);
	const setupFiles = useFernieSetupFiles({
		isActive,
		onJourneyStarted: () => dispatchOpensOnboarding("journeyStartedFromFiles"),
		sessionId
	});
	const migrationReview = useFernieMigrationReview({ journeyId: setupFiles.journeyId });
	const reportDraft = useFernieReportDraft({ isActive });

	const bottomRef = useRef<HTMLDivElement>(null);
	const conversationRef = useRef<HTMLDivElement>(null);
	const pinnedToBottom = useRef(true);
	const handleConversationScroll = () => {
		const container = conversationRef.current;
		if (container) pinnedToBottom.current = container.scrollHeight - container.scrollTop - container.clientHeight <= 80;
	};
	const inputRef = useRef<HTMLInputElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const surfacedExecutionCount = useRef(0);
	const pendingFeedbackMsgId = useRef<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);
	const latestLoadRef = useRef(0);

	const needsSession = shouldOpenSession({
		isActive,
		isSetupConversation: setupFiles.isSetupConversation,
		sessionId,
		view
	});
	useEffect(() => {
		if (!needsSession) return;
		let cancelled = false;
		openChatSession(chatId)
			.then((openedSessionId) => {
				if (shouldAdoptOpenedSession({ cancelled, sendInFlight: abortRef.current !== null }))
					setSessionId(openedSessionId);
			})
			.catch((error) => logger.warn("Failed to open Fernie chat session", error));
		return () => {
			cancelled = true;
		};
	}, [chatId, needsSession]);

	useEffect(() => {
		if (executedActions.length < surfacedExecutionCount.current) surfacedExecutionCount.current = 0;
		const clause = buildExecutionFeedback(executedActions.slice(surfacedExecutionCount.current));
		surfacedExecutionCount.current = executedActions.length;
		const targetId = pendingFeedbackMsgId.current;
		if (!clause || !targetId) return;
		setMessages((prev) => {
			if (!prev.some((m) => m.id === targetId)) return prev;
			return prev.map((m) => (m.id === targetId ? { ...m, content: `${m.content}\n\n${clause}`.trim() } : m));
		});
	}, [executedActions]);

	const lastMessage = messages.at(-1);
	useEffect(() => {
		if (messages.length && pinnedToBottom.current) bottomRef.current?.scrollIntoView({ behavior: "instant" });
	}, [messages.length, lastMessage]);
	const shouldFocusInput = focusOnOpen ?? isActive;
	useEffect(() => {
		if (shouldFocusInput && view === "chat") setTimeout(() => inputRef.current?.focus(), 200);
	}, [shouldFocusInput, view]);

	useEffect(() => {
		if (!isActive) return;
		let cancelled = false;
		setDeleteFailureNotice(null);
		fetchChatSessions()
			.then((sessions) => {
				if (cancelled) return;
				setServerSessions(sessions);
				setHasSessionsLoadFailed(false);
			})
			.catch((error) => {
				if (cancelled) return;
				logger.error("Failed to load Fernie chat sessions", error);
				setServerSessions(null);
				setHasSessionsLoadFailed(true);
			});
		return () => {
			cancelled = true;
		};
	}, [isActive, view]);

	useEffect(() => {
		if (messages.length === 0 || messages === resumed.messages) return;
		if (messages.some((message) => message.streaming)) return;
		setSaved((prev) => upsertSavedChat(prev, chatId, messages, sessionId));
	}, [chatId, messages, resumed.messages, sessionId]);

	useEffect(() => {
		if (messages.length > 0) setActiveSessionChatId(chatId);
	}, [chatId, messages.length]);

	const pushErrorMessage = (e: unknown) => {
		setRunOutcome("error");
		setMessages((m) => [...m, { content: `Error: ${toErrorMessage(e)}`, role: "assistant" }]);
	};

	const stampServerAnswer = async (turnSessionId: string, messageId: string) => {
		try {
			const answer = await getLatestAnswer(turnSessionId);
			if (!answer) return;
			setMessages((prev) =>
				answerRating.isNewServerAnswer(prev, answer.id)
					? prev.map((message) =>
							message.id === messageId
								? { ...message, rating: answer.rating ?? undefined, serverId: answer.id }
								: message
						)
					: prev
			);
		} catch (error: unknown) {
			logger.warn("Failed to read the Fernie answer id", error);
		}
	};

	const applyAssistantTurn = (
		turn: AssistantTurn,
		messageId: string,
		mode: "append" | "replace",
		turnSessionId: string | null
	) => {
		const built = buildAssistantMessage(turn, messageId, canOpenDestination);
		if (setupFiles.isSetupConversation)
			dispatch(
				apiEndpoints.util.invalidateTags([
					"OnboardingJourney",
					"TenantEmailDomain",
					"TenantEmailSenders",
					"TenantSmtpConnection"
				])
			);
		if (built.ranNavigation) dispatch(resetActionQueues());
		if (built.dispatchedActions.length > 0) dispatch(enqueueActions(built.dispatchedActions));
		pendingFeedbackMsgId.current = built.feedbackTargetId;
		setMessages((prev) =>
			mode === "append"
				? [...prev, built.message]
				: prev.map((message) => (message.id === messageId && message.streaming ? built.message : message))
		);
		if (turnSessionId) void stampServerAnswer(turnSessionId, messageId);
	};

	const appendAssistantTurn = (turn: AssistantTurn) => applyAssistantTurn(turn, wsId(), "append", sessionId);

	const appendStreamDelta = (messageId: string, chunk: string) =>
		setMessages((prev) =>
			prev.map((message) =>
				message.id === messageId && message.streaming ? { ...message, content: message.content + chunk } : message
			)
		);

	const applyStreamStep = (messageId: string, step: StreamStepEvent) =>
		setMessages((prev) =>
			prev.map((message) =>
				message.id === messageId && message.streaming
					? { ...message, steps: mergeStreamStep(message.steps ?? [], step) }
					: message
			)
		);

	const finalizeStreamError = (messageId: string, errorMessage: string) => {
		setRunOutcome("error");
		setMessages((prev) =>
			prev.map((message) =>
				message.id === messageId && message.streaming
					? {
							...message,
							content: composeStreamError(message.content, errorMessage),
							steps: undefined,
							streaming: false
						}
					: message
			)
		);
	};

	const runMutationFallback = async (
		turnInput: ChatTurnInput,
		activeModule: ChatModule | undefined,
		messageId: string,
		signal: AbortSignal,
		screenContext: ScreenContext
	) => {
		try {
			const result = await sendChatMessage(turnInput, sessionId ?? undefined, activeModule, screenContext);
			if (signal.aborted) return;
			setRunOutcome("success");
			setSessionId(result.id);
			applyAssistantTurn(result, messageId, "replace", result.id);
		} catch (error: unknown) {
			finalizeStreamError(messageId, toErrorMessage(error));
		}
	};

	const send = async (text: string, options?: SendOptions) => {
		latestLoadRef.current++;
		const file = options?.keepComposer ? null : pendingFile;
		if ((!text.trim() && !file) || loading) return;
		pinnedToBottom.current = true;

		const gridContext = chatTurn.buildGridContext(activeGridDataKey, activeGridSchema);
		const entityContext = buildEntityContext(activeEntity);
		const turnInput = chatTurn.buildTurnInput(text, file, gridContext, entityContext);

		if (attachedContextLimit.isAttachedContextTooLong(turnInput.attachedContext)) {
			setMessages((m) => [
				...m,
				{
					content: attachedContextLimit.buildAttachedContextTooLargeMessage(
						file?.name ?? null,
						Boolean(gridContext || entityContext)
					),
					role: "assistant"
				}
			]);
			return;
		}

		if (!options?.keepComposer) setInput("");
		let displayText = turnInput.message;
		let fileName: string | undefined;

		if (file) {
			const fileDisplay = chatTurn.buildFileMessageDisplay(file, turnInput.message);
			displayText = fileDisplay.displayText;
			fileName = fileDisplay.fileName;
			setPendingFile(null);
		}

		const assistantMsgId = wsId();
		setMessages((m) => [
			...m,
			{ content: displayText, fileName, role: "user" },
			{ content: "", id: assistantMsgId, role: "assistant", steps: [], streaming: true }
		]);
		setRunOutcome(null);
		setLoading(true);

		const controller = new AbortController();
		abortRef.current = controller;
		const activeModule = migrationReviewRules.turnModule({
			dockModule: getDockShellRoute(pathname)?.module,
			...migrationReviewRules.turnInputs(migrationReview, { ...options, opensOnboardingNext })
		});
		dispatchOpensOnboarding("sent");
		const screenContext = buildScreenContext(pathname, store.getState());
		let streamed = false;

		try {
			await streamChatMessage(
				turnInput,
				sessionId ?? null,
				activeModule,
				{
					onComplete: (result) => {
						setRunOutcome("success");
						setSessionId(result.id);
						applyAssistantTurn(result, assistantMsgId, "replace", result.id);
					},
					onDelta: (chunk) => {
						streamed = true;
						appendStreamDelta(assistantMsgId, chunk);
					},
					onError: (errorMessage) => finalizeStreamError(assistantMsgId, errorMessage),
					onSession: (id) => setSessionId(id),
					onStep: (step) => {
						streamed = true;
						applyStreamStep(assistantMsgId, step);
					}
				},
				controller.signal,
				screenContext
			);
		} catch (error: unknown) {
			if (!controller.signal.aborted) {
				if (error instanceof StreamConnectionError && !streamed) {
					await runMutationFallback(turnInput, activeModule, assistantMsgId, controller.signal, screenContext);
				} else {
					finalizeStreamError(assistantMsgId, toErrorMessage(error));
				}
			}
		} finally {
			if (abortRef.current === controller) abortRef.current = null;
			setLoading(false);
		}
	};

	const handleCancelStream = () => {
		abortRef.current?.abort();
		abortRef.current = null;
		setMessages((prev) =>
			prev.map((message) =>
				message.streaming ? { ...message, cancelled: true, steps: undefined, streaming: false } : message
			)
		);
		setRunOutcome(null);
		setLoading(false);
	};

	const handleRegenerateReport = (filename: string) => {
		const reportName = filename.replace(/\.[^.]+$/, "");
		send(`Regenerate the "${reportName}" report.`);
	};

	const updateProposal = (proposalId: string, patch: Partial<MutationProposal>) =>
		setMessages((prev) =>
			prev.map((msg) =>
				msg.proposals
					? { ...msg, proposals: msg.proposals.map((p) => (p.id === proposalId ? { ...p, ...patch } : p)) }
					: msg
			)
		);

	const handleConfirmProposal = async (proposalId: string) => {
		if (busyProposalId) return;
		setBusyProposalId(proposalId);
		try {
			const resolution = await confirmMutation(proposalId);
			updateProposal(proposalId, {
				cancelNote: undefined,
				priceImportApply: resolution.priceImportApply ?? undefined,
				resultSummary: resolution.summary,
				state: resolution.state
			});
			if (resolution.continuation) appendAssistantTurn(resolution.continuation);
		} catch (e: unknown) {
			logger.error(e);
			if (unauthorizedRetryPolicy.isMutationNotReplayedError(e)) pushErrorMessage(e);
			else updateProposal(proposalId, { resultSummary: `Failed: ${toErrorMessage(e)}`, state: "FAILED" });
		}
		setBusyProposalId(null);
	};

	const handleCancelProposal = async (proposalId: string) => {
		if (busyProposalId) return;
		setBusyProposalId(proposalId);
		try {
			const resolution = await cancelMutation(proposalId);
			updateProposal(proposalId, { cancelNote: undefined, resultSummary: resolution.summary, state: resolution.state });
		} catch (e: unknown) {
			logger.error(e);
			if (messages.some((msg) => msg.proposals?.some((p) => p.id === proposalId && p.priceImport)))
				updateProposal(proposalId, { cancelNote: toErrorMessage(e) });
			else pushErrorMessage(e);
		}
		setBusyProposalId(null);
	};

	const updateIntakeDraft = (captureId: string, patch: Partial<IntakeDraft>) =>
		setMessages((prev) =>
			prev.map((msg) =>
				msg.intakeDrafts
					? {
							...msg,
							intakeDrafts: msg.intakeDrafts.map((draft) =>
								draft.captureId === captureId ? { ...draft, ...patch } : draft
							)
						}
					: msg
			)
		);

	const findIntakeDraft = (captureId: string): IntakeDraft | undefined => {
		for (const msg of messages) {
			const draft = msg.intakeDrafts?.find((candidate) => candidate.captureId === captureId);
			if (draft) return draft;
		}
		return undefined;
	};

	const handleSubmitIntake = async (captureId: string) => {
		if (busyCaptureId) return;
		const draft = findIntakeDraft(captureId);
		if (!draft) return;
		setBusyCaptureId(captureId);
		try {
			const resolution = await confirmMutation(captureId);
			if (isSubmittedIntake(resolution.intake))
				updateIntakeDraft(captureId, { resolution: resolveIntakeSubmission(resolution.intake, draft) });
			if (resolution.continuation) appendAssistantTurn(resolution.continuation);
		} catch (e: unknown) {
			logger.error(e);
			if (unauthorizedRetryPolicy.isMutationNotReplayedError(e)) pushErrorMessage(e);
			else updateIntakeDraft(captureId, { resolution: declineResolution(draft) });
		}
		setBusyCaptureId(null);
	};

	const handleCancelIntake = async (captureId: string) => {
		if (busyCaptureId) return;
		setBusyCaptureId(captureId);
		try {
			await cancelMutation(captureId);
			setMessages((prev) =>
				prev.map((msg) =>
					msg.intakeDrafts
						? { ...msg, intakeDrafts: msg.intakeDrafts.filter((draft) => draft.captureId !== captureId) }
						: msg
				)
			);
		} catch (e: unknown) {
			logger.error(e);
			pushErrorMessage(e);
		}
		setBusyCaptureId(null);
	};

	const handleChooseNav = (msgId: string, candidate: NavCandidate) => {
		setMessages((prev) =>
			prev.map((m) => (m.id === msgId && m.chosenNavPath === undefined ? { ...m, chosenNavPath: candidate.path } : m))
		);
		if (isExternalPath(candidate.path)) {
			window.open(candidate.path, "_blank", "noopener,noreferrer");
			return;
		}
		pendingFeedbackMsgId.current = msgId;
		dispatch(resetActionQueues());
		dispatch(enqueueActions([{ path: candidate.path, type: "navigateTo" }]));
	};

	const handleRateMessage = async (
		message: Message,
		picked: AnswerRating,
		details?: RatingDetails
	): Promise<boolean> => {
		if (!sessionId || !message.serverId) return false;
		const messageId = message.serverId;
		if (ratingPendingIds.includes(messageId)) return false;
		const previous = message.rating;
		const next = details === undefined ? answerRating.nextRating(previous, picked) : picked;
		const setRating = (rating: AnswerRating | undefined) =>
			setMessages((prev) => prev.map((item) => (item.serverId === messageId ? { ...item, rating } : item)));
		setRating(next ?? undefined);
		setRatingPendingIds((ids) => [...ids, messageId]);
		try {
			setRating((await rateChatMessage(sessionId, messageId, next, details)) ?? undefined);
			return true;
		} catch (e: unknown) {
			logger.error(e);
			setRating(previous);
			pushErrorMessage(e);
			return false;
		} finally {
			setRatingPendingIds((ids) => ids.filter((id) => id !== messageId));
		}
	};

	const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file && (reportDraft.isStarted || (!setupFiles.acceptsSetupFiles && reportDraft.takesChatFile(file.name)))) {
			e.target.value = "";
			reportDraft.attachFile(file);
			return;
		}
		if (setupFiles.acceptsSetupFiles) {
			const files = Array.from(e.target.files ?? []);
			e.target.value = "";
			await setupFiles.attachFiles(files);
			return;
		}
		if (!file) return;
		e.target.value = "";
		try {
			const content = await parseFileToText(file);
			if (content.length > attachedContextLimit.MAX_ATTACHED_CONTEXT_LENGTH) {
				setMessages((m) => [
					...m,
					{ content: attachedContextLimit.buildAttachedContextTooLargeMessage(file.name, false), role: "assistant" }
				]);
				return;
			}
			setPendingFile({ content, name: file.name, size: file.size });
			inputRef.current?.focus();
		} catch (err: unknown) {
			setMessages((m) => [...m, { content: `Error: ${toErrorMessage(err)}`, role: "assistant" }]);
		}
	};

	const resetActiveThread = () => {
		latestLoadRef.current++;
		clearActiveSessionChatId();
		setMessages([]);
		setSessionId(null);
		setPendingFile(null);
		reportDraft.reset();
		dispatchOpensOnboarding("newChat");
		setChatId(wsId());
		setInput("");
	};

	const handleNewChat = () => {
		resetActiveThread();
		setView("chat");
	};

	const openChat = (openedChatId: string, openedSessionId: string | null, openedMessages: Message[]) => {
		setMessages(openedMessages);
		setSessionId(openedSessionId);
		setChatId(openedChatId);
		setPendingFile(null);
		reportDraft.reset();
		dispatchOpensOnboarding("openedChat");
		setView("chat");
	};

	const openCachedChat = (cachedChat: SavedChat) => openChat(cachedChat.id, cachedChat.sessionId, cachedChat.messages);

	const handleLoadChat = async ({ cachedChat, sessionId: rowSessionId }: HistoryRow) => {
		setConfirmingDeleteId(null);
		const load = ++latestLoadRef.current;
		try {
			const serverMessages = rowSessionId ? await getChatSessionHistory(rowSessionId) : [];
			if (load !== latestLoadRef.current) return;
			if (cachedChat && holdsEveryServerMessage(cachedChat.messages, serverMessages))
				return openChat(
					cachedChat.id,
					cachedChat.sessionId,
					withServerAnswerFields(cachedChat.messages, serverMessages)
				);
			openChat(cachedChat?.id ?? wsId(), rowSessionId, mapServerMessages(serverMessages));
		} catch (e: unknown) {
			logger.error("Failed to load Fernie chat history", e);
			if (load !== latestLoadRef.current) return;
			if (cachedChat) return openCachedChat(cachedChat);
			openChat(wsId(), rowSessionId, []);
			pushErrorMessage(e);
		}
	};

	const handleDeleteChat = async ({ cachedChat, sessionId: rowSessionId }: HistoryRow) => {
		setConfirmingDeleteId(null);
		latestLoadRef.current++;
		if (rowSessionId) {
			try {
				await deleteChatSession(rowSessionId);
			} catch (e: unknown) {
				logger.error("Failed to delete Fernie chat session", e);
				setDeleteFailureNotice(
					unauthorizedRetryPolicy.isMutationNotReplayedError(e)
						? MUTATION_NOT_REPLAYED_MESSAGE
						: t("fernieChat.panel.deleteFailed")
				);
				return;
			}
		}
		setDeleteFailureNotice(null);
		setServerSessions((prev) => prev && prev.filter((s) => s.id !== rowSessionId));
		setSaved((prev) => {
			const updated = prev.filter((c) => c.id !== cachedChat?.id);
			persistSaved(updated);
			return updated;
		});
		if (cachedChat?.id === chatId) resetActiveThread();
	};

	const appendMessages = (next: Message[]) => {
		setMessages((prev) => [...prev, ...next]);
	};

	const replaceAssistantMessage = (messageId: string, content: string) => {
		setMessages((prev) =>
			prev.map((message) => (message.id === messageId ? { ...message, content, streaming: false } : message))
		);
	};

	const suggestions = useMemo(() => buildWelcomeSuggestions(welcomeCustomerName), [welcomeCustomerName, language]);
	const routeWalkthroughs = useAppSelector((state) => findWalkthroughsForRoute(pathname, state), shallowEqual);
	const historyRows = useMemo(() => buildHistoryRows(serverSessions, saved), [serverSessions, saved]);
	const historyNotice =
		deleteFailureNotice ?? (hasSessionsLoadFailed ? t("fernieChat.panel.historyUnavailable") : null);
	const hasHistory = historyRows.length > 0;
	const historyLabel = hasHistory
		? t("fernieChat.panel.historySaved", { count: historyRows.length })
		: t("fernieChat.panel.history");

	const activeSteps = messages.find((message) => message.streaming)?.steps ?? [];
	const lastAssistantIndex = messages.reduce(
		(last, message, index) => (message.role === "assistant" ? index : last),
		-1
	);
	const faceState = useFernieFaceState({
		isComposing: inputFocused || input.trim().length > 0,
		isRunActive: loading,
		outcome: loading ? null : runOutcome,
		steps: activeSteps
	});

	return {
		bottomRef,
		busyCaptureId,
		busyProposalId,
		chatId,
		confirmingDeleteId,
		conversationRef,
		faceState,
		fileInputRef,
		handleCancelIntake,
		handleCancelProposal,
		handleCancelStream,
		handleChooseNav,
		handleConfirmProposal,
		handleConversationScroll,
		handleDeleteChat,
		handleFile,
		handleLoadChat,
		handleNewChat,
		handleRateMessage,
		handleRegenerateReport,
		handleSubmitIntake,
		hasHistory,
		historyLabel,
		historyNotice,
		historyRows,
		input,
		inputRef,
		lastAssistantIndex,
		loading,
		messages,
		migrationReview,
		pendingFile,
		ratingPendingIds,
		reportDraft,
		routeWalkthroughs,
		send,
		setConfirmingDeleteId,
		setInput,
		setInputFocused,
		setPendingFile,
		setRunOutcome,
		setupFiles,
		sessionId,
		appendMessages,
		replaceAssistantMessage,
		setView,
		suggestions,
		view
	};
};

export default useFernieChat;
